/*
 * bt-predictive.js — header search suggestions from Shopify's Predictive Search API
 * (FINAL-SPEC §5.5, §5.2, §8.8, §6.4; task C6). Loaded once with `defer` from snippets/header-scripts.liquid
 * (2026-10; it used to be tagged by both header-search snippets). The window.btPredictive guard stays.
 *
 * What it does
 *   - listens (delegated) to every `.js_iput_search` field: 250 ms debounce, ≥2 characters, then
 *     GET {root}search/suggest.json — one call PER resource type, in parallel, because
 *     `resources[limit]` is a TOTAL budget shared by every requested type (a single
 *     `type=product,collection,article,query&limit=6` call returns only ~2 products, so Stella /
 *     Twinpower never reach the panel — QA SEARCH-01). So:
 *         products     …&resources[type]=product&resources[limit]=6
 *                      &resources[options][fields]=title,product_type,variants.sku,vendor
 *                      &resources[options][unavailable_products]=last
 *         collections  …&resources[type]=collection&resources[limit]=3
 *         articles     …&resources[type]=article&resources[limit]=2
 *         queries      …&resources[type]=query&resources[limit]=4   — fetched ONLY when the product
 *                      call came back empty, since render() discards completions otherwise.
 *     The responses are merged into one Predictive-Search-shaped object and cached under the query
 *     (in-flight requests aborted on a new keystroke).
 *   - renders into the field's panel ([data-bt-predictive] next to the form): Collections (≤3, "UK stock (N)"
 *     injected FIRST when the query matches /uk|stock|quick/i and N > 0 — N and the URL come from the form's
 *     data-ukstock-* attributes, printed server-side), Products (≤6 compact rows: 48 px image, model +
 *     descriptor split like snippets/bt-product-title, price, route line), Guides (≤2), footer
 *     "See all results for “q” →" with the CLEAN query (no `*`, see searchURL in jquery.interact-function).
 *   - open  = remove `hidden` + `inert`, add .is-open (+ .open_result on the wrap for the theme's CSS)
 *     close = the reverse, so a closed panel's links never enter the tab order (audit ST-02).
 *   - keyboard: ArrowDown/ArrowUp move aria-activedescendant through the options, Enter follows the
 *     active one, Esc closes (second Esc clears), Tab leaves naturally; outside click / focus-out close.
 *   - the empty state (server-rendered chip row) opens on pointer focus or typing, NOT on keyboard Tab
 *     focus, so tabbing through the header does not add five chip stops before the nav.
 *   - bottom-bar Search ([data-bt-search-trigger], sections/menu-mobile-bottom): scroll to top + focus
 *     row B; when row B is collapsed (body.scrollDown → 0 px) the theme's push_side opens #search_pupop.
 *   - #search_pupop (templates/search.ajax-product) has no [data-bt-predictive]; its form gets the same
 *     suggestions rendered into its legacy `.js_prs_search` container (config falls back to the first
 *     [data-bt-search] form on the page).
 *
 * Honesty (§14): the ONLY delivery copy printed here mirrors snippets/bt-route-line.liquid, tags-only,
 * exclusive order mcf → uk-stock → non-JDM vendor (data-bt-nonjdm) → Japan default. Sold-out products
 * get a muted "Sold out" chip and no route line. On the `tr` locale no route line is printed at all
 * (bt-route-line renders nothing there either). No counts, countdowns or reference prices.
 */
(function () {
  'use strict';
  if (window.btPredictive) return;

  var DEBOUNCE = 250,
      MIN_CHARS = 2,
      LIMIT = 6,            /* product rows (§5.5) — also the product call's own resources[limit] */
      COL_LIMIT = 3,        /* Collections group (§5.5: "up to 3") */
      ART_LIMIT = 2,        /* Guides group (§5.5: "(2)") */
      QUERY_LIMIT = 4,      /* completion chips, only when no product matched */
      UK_RE = /uk|stock|quick/i,
      cache = new Map(),
      inflight = null,
      seq = 0,
      timer = null,
      pointerFocus = false,
      reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- helpers ---------- */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function fmt(tpl, q) { return String(tpl || '').replace('%s', esc(q)); }
  function defaultForm() { return document.querySelector('form[data-bt-search]'); }
  function ctxFor(form) {
    var src = form.hasAttribute('data-bt-search') ? form : (defaultForm() || form),
        d = src.dataset || {},
        root = d.btRoot || (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return {
      root: root,
      locale: (d.btLocale || document.documentElement.lang || 'en').toLowerCase(),
      isTr: (d.btLocale || document.documentElement.lang || '').toLowerCase().indexOf('tr') === 0,
      currency: d.btCurrency || (window.theme && window.theme.currency) || 'GBP',
      ukCount: parseInt(d.ukstockCount, 10) || 0,
      ukUrl: d.ukstockUrl || (root + 'collections/uk-stock'),
      nonJdm: (d.btNonjdm || '').split('|').map(function (v) { return v.trim().toLowerCase(); }).filter(Boolean),
      l: {
        collections: d.btLCollections || 'Collections',
        products: d.btLProducts || 'Products',
        guides: d.btLGuides || 'Guides',
        suggest: d.btLSuggest || 'Suggestions',
        seeall: d.btLSeeall || 'See all results for "%s"',
        none: d.btLNone || 'No matches for "%s".',
        soldout: d.btLSoldout || 'Sold out',
        ukstock: d.btLUkstock || 'UK stock'
      }
    };
  }
  var moneyCache = {};
  function money(amount, ctx) {
    var n = parseFloat(amount);
    if (isNaN(n)) return '';
    var key = ctx.currency + '|' + ctx.locale;
    try {
      if (!moneyCache[key]) {
        moneyCache[key] = new Intl.NumberFormat(ctx.isTr ? 'tr-TR' : 'en-GB', { style: 'currency', currency: ctx.currency });
      }
      return moneyCache[key].format(n);
    } catch (e) {
      return ctx.currency + ' ' + n.toFixed(2);
    }
  }
  function imgUrl(p) {
    var u = (p.featured_image && p.featured_image.url) || p.image || '';
    if (!u) return '';
    return u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=96';
  }

  /* Model / descriptor split — the JS mirror of snippets/bt-product-title.liquid (display only). */
  function caseToken(w) {
    if (/\d/.test(w)) return w;
    if (w.length <= 3) return w;
    var lead = '', body = w;
    if (body.charAt(0) === '(') { lead = '('; body = body.slice(1); }
    if (body.indexOf('-') > -1 && body === body.toUpperCase()) return lead + body;
    return lead + body.charAt(0).toUpperCase() + body.slice(1).toLowerCase();
  }
  function splitTitle(title, vendor) {
    var src = String(title || '').trim(),
        brandTokens = 0;
    if (vendor) {
      var v = String(vendor).trim().toLowerCase(), s = src.toLowerCase();
      if (s === v || s.indexOf(v + ' ') === 0) brandTokens = v.split(' ').length;
    }
    var seps = [' : ', ' #', ' - ', ' / '], cut = -1, skip = 0;
    seps.forEach(function (sep) {
      var i = src.indexOf(sep);
      if (i < 0) return;
      var leftTokens = src.slice(0, i).trim().split(/\s+/).length;
      if (leftTokens >= brandTokens + 1 && (cut < 0 || i < cut)) { cut = i; skip = sep === ' #' ? 1 : sep.length; }
    });
    var left = cut < 0 ? src : src.slice(0, cut),
        right = cut < 0 ? '' : src.slice(cut + skip),
        seen = {}, model = [], desc = [];
    left.split(/\s+/).forEach(function (w, i) {
      if (!w) return;
      var k = w.toLowerCase();
      if (i < brandTokens) { seen[k] = 1; return; }
      if (seen[k]) return;
      seen[k] = 1;
      model.push(caseToken(w));
    });
    right.split(/\s+/).forEach(function (w) {
      if (!w) return;
      var k = w.toLowerCase();
      if (seen[k]) return;
      seen[k] = 1;
      desc.push(w === '-' ? '–' : caseToken(w));
    });
    return { model: model.join(' ') || src, desc: desc.join(' ') };
  }

  /* Route line — same branch order as bt-route-line.liquid. Returns '' on tr. */
  function routeHtml(p, ctx) {
    if (ctx.isTr) return '';
    var tags = (p.tags || []).map(function (t) { return String(t).toLowerCase(); }),
        vendor = String(p.vendor || '').trim().toLowerCase(),
        route = 'jp', text;
    if (tags.indexOf('mcf') > -1) route = 'mcf';
    else if (tags.indexOf('uk-stock') > -1) route = 'uk';
    else if (vendor && ctx.nonJdm.indexOf(vendor) > -1) route = 'supplier';
    if (route === 'mcf') text = 'UK warehouse &middot; 2&ndash;3 working days';
    else if (route === 'uk') text = 'UK stock &middot; 1&ndash;2 working days';
    else if (route === 'supplier') text = 'Ordered from our supplier &middot; 7&ndash;14 working days';
    else text = 'Direct from Japan &middot; 7&ndash;14 working days';
    if (route === 'uk' || route === 'mcf') {
      return '<span class="bt-route bt-route--' + route + ' bt-route--card bt-route--pill t-badge" data-bt-route="' + route + '">' +
        (route === 'uk' ? '<span class="bt-route__dot" aria-hidden="true"></span>' : '') +
        '<span class="bt-route__text">' + text + '</span></span>';
    }
    return '<span class="bt-route bt-route--' + route + ' bt-route--card bt-route--plain" data-bt-route="' + route + '"><span class="bt-route__text">' + text + '</span></span>';
  }

  /* ---------- DOM lookups ---------- */
  function wrapOf(form) { return form.closest('.mini_cart_wrap') || form.parentNode; }
  function panelOf(form) {
    var id = form.getAttribute('data-bt-panel');
    return (id && document.getElementById(id)) || wrapOf(form).querySelector('[data-bt-predictive]');
  }
  function legacyOf(form) { return wrapOf(form).querySelector('.js_prs_search'); }
  function resultsOf(form) {
    var panel = panelOf(form);
    if (panel) return panel.querySelector('[data-bt-results]');
    return legacyOf(form);
  }
  function inputOf(form) { return form.querySelector('.js_iput_search'); }

  function setOpen(form, open) {
    var wrap = wrapOf(form), panel = panelOf(form), input = inputOf(form), legacy = legacyOf(form);
    if (panel) {
      if (open) {
        panel.hidden = false;
        panel.removeAttribute('inert');
        panel.classList.add('is-open');
      } else {
        panel.classList.remove('is-open');
        panel.hidden = true;
        panel.setAttribute('inert', '');
      }
    } else if (legacy) {
      var skel = wrap.querySelector('.skeleton_js');
      if (skel) skel.classList.add('d-none-important');
      var prs = wrap.querySelector('.search_header__prs');
      if (prs) prs.style.display = 'none';
      legacy.style.display = open ? '' : 'none';
    }
    wrap.classList.toggle('open_result', open);
    var laber = form.closest('.laber_search');
    if (laber && laber !== wrap) laber.classList.toggle('open_result', open);
    if (input) input.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (!open) setActive(form, null);
  }
  function isOpen(form) {
    var panel = panelOf(form);
    return panel ? !panel.hidden : wrapOf(form).classList.contains('open_result');
  }
  function showEmpty(form) {
    var panel = panelOf(form);
    if (!panel) return false;
    var empty = panel.querySelector('[data-bt-empty]'), results = panel.querySelector('[data-bt-results]');
    if (!empty || !empty.querySelector('a')) return false;
    if (results) results.innerHTML = '';
    empty.hidden = false;
    setOpen(form, true);
    return true;
  }
  function showResults(form) {
    var panel = panelOf(form);
    if (panel) {
      var empty = panel.querySelector('[data-bt-empty]');
      if (empty) empty.hidden = true;
    }
    setOpen(form, true);
  }

  /* ---------- active option (aria-activedescendant) ---------- */
  function options(form) {
    var r = resultsOf(form);
    return r ? Array.prototype.slice.call(r.querySelectorAll('[role="option"]')) : [];
  }
  function setActive(form, el) {
    var input = inputOf(form);
    options(form).forEach(function (o) { o.setAttribute('aria-selected', o === el ? 'true' : 'false'); o.classList.toggle('is-active', o === el); });
    if (!input) return;
    if (el) {
      input.setAttribute('aria-activedescendant', el.id);
      if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
    } else {
      input.removeAttribute('aria-activedescendant');
    }
  }
  function activeOf(form) {
    var input = inputOf(form), id = input && input.getAttribute('aria-activedescendant');
    return id ? document.getElementById(id) : null;
  }
  function move(form, dir) {
    var list = options(form);
    if (!list.length) return;
    var cur = list.indexOf(activeOf(form)), next;
    if (cur < 0) next = dir > 0 ? 0 : list.length - 1;
    else next = (cur + dir + list.length) % list.length;
    setActive(form, list[next]);
  }

  /* ---------- rendering ---------- */
  function seeAllUrl(form, q) {
    var params = new URLSearchParams();
    Array.prototype.forEach.call(form.querySelectorAll('input[type="hidden"][name], select[name]'), function (el) {
      if (el.name && el.value && el.value !== '*') params.append(el.name, el.value);
    });
    params.set('q', q);
    return form.getAttribute('action') + '?' + params.toString();
  }
  function render(form, q, data) {
    var ctx = ctxFor(form),
        r = (data && data.resources && data.resources.results) || {},
        products = r.products || [],
        collections = (r.collections || []).slice(),
        articles = (r.articles || []).slice(0, ART_LIMIT),
        queries = r.queries || [],
        uid = (form.getAttribute('data-bt-panel') || 'bt-ps') + '-',
        html = '', n = 0, count = 0;

    /* Collections — the UK-stock pool goes first on an "uk / stock / quick" query, once, only at N > 0. */
    var hasUk = collections.some(function (c) { return c.handle === 'uk-stock'; });
    var cols = [];
    if (ctx.ukCount > 0 && !ctx.isTr && UK_RE.test(q)) {
      cols.push({ title: ctx.l.ukstock + ' (' + ctx.ukCount + ')', url: ctx.ukUrl, handle: 'uk-stock', uk: true });
      collections = collections.filter(function (c) { return c.handle !== 'uk-stock'; });
    } else if (hasUk && ctx.ukCount > 0 && !ctx.isTr) {
      collections = collections.map(function (c) {
        return c.handle === 'uk-stock' ? { title: ctx.l.ukstock + ' (' + ctx.ukCount + ')', url: c.url, handle: c.handle, uk: true } : c;
      });
    } else if (hasUk) {
      collections = collections.filter(function (c) { return c.handle !== 'uk-stock'; });
    }
    cols = cols.concat(collections).slice(0, COL_LIMIT);
    if (cols.length) {
      html += '<div class="bt-ps__group" role="group" aria-labelledby="' + uid + 'hc"><div class="bt-ps__hd t-eyebrow" id="' + uid + 'hc">' + esc(ctx.l.collections) + '</div><ul class="bt-ps__list">';
      cols.forEach(function (c) {
        n++; count++;
        html += '<li role="option" id="' + uid + n + '" class="bt-ps__item" aria-selected="false"><a class="bt-ps__link bt-ps__col' + (c.uk ? ' bt-ps__col--uk' : '') + '" href="' + esc(c.url) + '">' +
          (c.uk ? '<span class="bt-ps__dot" aria-hidden="true"></span>' : '') + esc(c.title) + '</a></li>';
      });
      html += '</ul></div>';
    }

    /* Products */
    if (products.length) {
      html += '<div class="bt-ps__group" role="group" aria-labelledby="' + uid + 'hp"><div class="bt-ps__hd t-eyebrow" id="' + uid + 'hp">' + esc(ctx.l.products) + '</div><ul class="bt-ps__list">';
      products.slice(0, LIMIT).forEach(function (p) {
        n++; count++;
        var t = splitTitle(p.title, p.vendor), img = imgUrl(p),
            sold = p.available === false;
        html += '<li role="option" id="' + uid + n + '" class="bt-ps__item" aria-selected="false"><a class="bt-ps__link bt-ps__product' + (sold ? ' bt-ps__product--sold' : '') + '" href="' + esc(p.url) + '">' +
          (img ? '<img class="bt-ps__img" src="' + esc(img) + '" width="48" height="48" alt="" loading="lazy" decoding="async">' : '<span class="bt-ps__img bt-ps__img--none" aria-hidden="true"></span>') +
          '<span class="bt-ps__body">' +
            '<span class="bt-ps__title"><span class="bt-title__model">' + esc(t.model) + '</span>' + (t.desc ? ' <span class="bt-title__desc">' + esc(t.desc) + '</span>' : '') + '</span>' +
            '<span class="bt-ps__meta"><span class="bt-ps__price t-num">' + esc(money(p.price, ctx)) + '</span>' +
              (sold ? '<span class="bt-ps__sold">' + esc(ctx.l.soldout) + '</span>' : routeHtml(p, ctx)) +
            '</span>' +
          '</span></a></li>';
      });
      html += '</ul></div>';
    } else if (queries.length) {
      /* No product hit: Shopify's query completions are the most useful next step. */
      html += '<div class="bt-ps__group" role="group" aria-labelledby="' + uid + 'hq"><div class="bt-ps__hd t-eyebrow" id="' + uid + 'hq">' + esc(ctx.l.suggest) + '</div><ul class="bt-ps__list bt-ps__list--chips">';
      queries.slice(0, QUERY_LIMIT).forEach(function (s) {
        n++; count++;
        html += '<li role="option" id="' + uid + n + '" class="bt-ps__item" aria-selected="false"><a class="bt-chip" href="' + esc(seeAllUrl(form, s.text)) + '">' + esc(s.text) + '</a></li>';
      });
      html += '</ul></div>';
    }

    /* Guides */
    if (articles.length) {
      html += '<div class="bt-ps__group" role="group" aria-labelledby="' + uid + 'hg"><div class="bt-ps__hd t-eyebrow" id="' + uid + 'hg">' + esc(ctx.l.guides) + '</div><ul class="bt-ps__list">';
      articles.forEach(function (a) {
        n++; count++;
        html += '<li role="option" id="' + uid + n + '" class="bt-ps__item" aria-selected="false"><a class="bt-ps__link bt-ps__guide" href="' + esc(a.url) + '">' + esc(a.title) + '</a></li>';
      });
      html += '</ul></div>';
    }

    if (!count) {
      html += '<p class="bt-ps__none">' + fmt(ctx.l.none, q) + '</p>';
    }
    n++;
    html += '<div class="bt-ps__foot" role="option" id="' + uid + n + '" aria-selected="false"><a class="bt-ps__all" href="' + esc(seeAllUrl(form, q)) + '">' + fmt(ctx.l.seeall, q) + ' <span aria-hidden="true">&rarr;</span></a></div>';

    var target = resultsOf(form);
    if (!target) return;
    target.innerHTML = html;
    var status = panelOf(form) && panelOf(form).querySelector('[data-bt-status]');
    if (status) status.textContent = count ? count + ' ' + (count === 1 ? 'suggestion' : 'suggestions') : fmt(ctx.l.none, q);
    showResults(form);
  }

  /* ---------- fetching ---------- */
  function bar(form, state) {
    var b = form.querySelector('.ld_bar_search');
    if (!b) return;
    if (state === 'start') { b.className = 'ld_bar_search on_star'; }
    else if (state === 'end') {
      b.classList.add('on_end');
      setTimeout(function () { b.className = 'ld_bar_search'; }, 280);
    } else { b.className = 'ld_bar_search'; }
  }
  /* One request per resource type: `resources[limit]` is a shared TOTAL, so a combined call
     starves every group (QA SEARCH-01). Product options are only meaningful on the product call. */
  function suggestUrl(ctx, q, type, limit) {
    var url = ctx.root + 'search/suggest.json?q=' + encodeURIComponent(q) +
      '&resources[type]=' + type + '&resources[limit]=' + limit;
    if (type === 'product') {
      url += '&resources[options][fields]=title,product_type,variants.sku,vendor' +
        '&resources[options][unavailable_products]=last';
    }
    return url;
  }
  function getJson(url, signal) {
    return fetch(url, { signal: signal, headers: { Accept: 'application/json' } })
      .then(function (res) { if (!res.ok) throw new Error('suggest ' + res.status); return res.json(); })
      .catch(function () { return null; }); /* one dead group must not blank the whole panel */
  }
  function resultsIn(data) { return (data && data.resources && data.resources.results) || {}; }
  function mergeResults(parts) {
    var out = { products: [], collections: [], articles: [], queries: [] };
    parts.forEach(function (d) {
      var r = resultsIn(d);
      Object.keys(out).forEach(function (k) { if (r[k] && r[k].length) out[k] = out[k].concat(r[k]); });
    });
    return { resources: { results: out } };
  }
  function search(form, q) {
    var ctx = ctxFor(form), key = ctx.root + '|' + q.toLowerCase();
    if (cache.has(key)) { render(form, q, cache.get(key)); return; }
    if (inflight) { inflight.abort(); inflight = null; }
    var my = ++seq, ctrl = ('AbortController' in window) ? new AbortController() : null,
        signal = ctrl ? ctrl.signal : undefined;
    inflight = ctrl;
    bar(form, 'start');
    Promise.all([
      getJson(suggestUrl(ctx, q, 'product', LIMIT), signal),
      getJson(suggestUrl(ctx, q, 'collection', COL_LIMIT), signal),
      getJson(suggestUrl(ctx, q, 'article', ART_LIMIT), signal)
    ])
      .then(function (parts) {
        if (my !== seq) return null; /* aborted / superseded keystroke */
        if ((resultsIn(parts[0]).products || []).length) return parts;
        /* no product hit — only then are Shopify's completions worth a round-trip */
        return getJson(suggestUrl(ctx, q, 'query', QUERY_LIMIT), signal).then(function (qd) {
          return my === seq ? parts.concat([qd]) : null;
        });
      })
      .then(function (parts) {
        if (!parts || my !== seq) return;
        var data = mergeResults(parts);
        /* never cache a total network failure — the old code did not cache errors either */
        if (parts.some(function (d) { return d !== null; })) {
          if (cache.size > 30) cache.delete(cache.keys().next().value);
          cache.set(key, data);
        }
        render(form, q, data); /* all dead → honest empty state + "See all results" fallback */
      })
      .then(function () { if (my === seq) { inflight = null; bar(form, 'end'); } });
  }
  function schedule(form) {
    var input = inputOf(form), q = (input.value || '').trim();
    clearTimeout(timer);
    toggleClear(form);
    if (q.length < MIN_CHARS) {
      seq++;
      if (inflight) { inflight.abort(); inflight = null; }
      bar(form, 'reset');
      if (!q.length) { if (!showEmpty(form)) setOpen(form, false); }
      else setOpen(form, false);
      return;
    }
    timer = setTimeout(function () { search(form, q); }, DEBOUNCE);
  }
  function toggleClear(form) {
    var btn = form.querySelector('.bt-search__clear'), input = inputOf(form);
    if (btn && input) btn.hidden = !(input.value && input.value.length);
  }

  /* ---------- events (delegated so #search_pupop's lazy-loaded form is covered) ---------- */
  document.addEventListener('input', function (e) {
    var input = e.target;
    if (!input.classList || !input.classList.contains('js_iput_search')) return;
    var form = input.closest('form');
    if (form) schedule(form);
  });

  document.addEventListener('pointerdown', function (e) {
    pointerFocus = !!(e.target.closest && e.target.closest('.js_iput_search'));
    /* outside click closes every open panel */
    Array.prototype.forEach.call(document.querySelectorAll('form.js_frm_search'), function (form) {
      if (!isOpen(form)) return;
      var wrap = wrapOf(form);
      if (!wrap.contains(e.target)) setOpen(form, false);
    });
  }, true);

  document.addEventListener('focusin', function (e) {
    var input = e.target;
    if (!input.classList || !input.classList.contains('js_iput_search')) return;
    var form = input.closest('form');
    if (!form) return;
    toggleClear(form);
    var q = (input.value || '').trim();
    if (q.length >= MIN_CHARS) { if (!isOpen(form)) search(form, q); }
    else if (!q.length && pointerFocus) showEmpty(form);
    pointerFocus = false;
  });

  document.addEventListener('focusout', function (e) {
    var form = e.target.closest && e.target.closest('form.js_frm_search');
    if (!form) {
      var panel = e.target.closest && e.target.closest('[data-bt-predictive]');
      if (!panel) return;
      form = wrapOf(panel).querySelector('form.js_frm_search');
      if (!form) return;
    }
    var wrap = wrapOf(form), to = e.relatedTarget;
    if (to && wrap.contains(to)) return;
    /* pointer-driven focus loss is handled by pointerdown; this catches Tab leaving the widget */
    setTimeout(function () {
      if (!wrap.contains(document.activeElement)) setOpen(form, false);
    }, 0);
  });

  document.addEventListener('keydown', function (e) {
    var form = e.target.closest && e.target.closest('form.js_frm_search');
    var panel = !form && e.target.closest && e.target.closest('[data-bt-predictive]');
    if (panel) form = wrapOf(panel).querySelector('form.js_frm_search');
    if (!form) return;
    var input = inputOf(form), inInput = e.target === input;
    if (e.key === 'Escape' || e.key === 'Esc') {
      if (isOpen(form)) {
        e.preventDefault();
        setOpen(form, false);
        if (input && !inInput) input.focus();
      } else if (inInput && input.value) {
        e.preventDefault();
        input.value = '';
        toggleClear(form);
      }
      return;
    }
    if (!inInput) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      var q = (input.value || '').trim();
      if (!isOpen(form)) {
        if (q.length >= MIN_CHARS) search(form, q); else showEmpty(form);
        e.preventDefault();
        return;
      }
      e.preventDefault();
      move(form, e.key === 'ArrowDown' ? 1 : -1);
    } else if (e.key === 'Enter') {
      var act = activeOf(form), link = act && act.querySelector('a');
      if (link && link.href) { e.preventDefault(); window.location.href = link.href; }
    }
  });

  document.addEventListener('click', function (e) {
    var clear = e.target.closest && e.target.closest('.bt-search__clear');
    if (clear) {
      var form = clear.closest('form'), input = form && inputOf(form);
      if (!input) return;
      input.value = '';
      toggleClear(form);
      setOpen(form, false);
      input.focus();
      return;
    }
    /* a chosen suggestion closes the panel before navigation (back/forward cache restores it closed) */
    var link = e.target.closest && e.target.closest('[data-bt-predictive] a');
    if (link) {
      var f = wrapOf(link.closest('[data-bt-predictive]')).querySelector('form.js_frm_search');
      if (f) setOpen(f, false);
    }
  });

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form.classList || !form.classList.contains('js_frm_search')) return;
    var input = inputOf(form);
    if (input && !(input.value || '').trim()) { e.preventDefault(); input.focus(); return; }
    clearTimeout(timer);
    setOpen(form, false);
  });

  /* Bottom-bar "Search" cell (sections/menu-mobile-bottom): row B visible → scroll up + focus it;
     row B collapsed by body.scrollDown → focus it where the reader is (:focus-within expands it);
     row B not rendered → the search page. It never falls through to the theme's push_side any more:
     #search_pupop's lazy include is never fetched, so it opened as an empty full-screen layer that
     neither Escape nor a tap closed (found 2026-10-10, phone, any scrolled page). */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-bt-search-trigger]');
    if (!btn) return;
    var rowB = document.querySelector('.aloSearch-mobile .js_iput_search'),
        row = rowB && rowB.closest('.aloSearch-mobile');
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    if (!rowB || !row) {
      window.location.href = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'search';
      return;
    }
    if (row.offsetHeight === 0) {
      pointerFocus = true;
      try { rowB.focus({ preventScroll: true }); } catch (err) { rowB.focus(); }
      return;
    }
    var smooth = !(reduced && reduced.matches);
    try { window.scrollTo({ top: 0, behavior: smooth ? 'smooth' : 'auto' }); } catch (err) { window.scrollTo(0, 0); }
    pointerFocus = true;
    setTimeout(function () {
      try { rowB.focus({ preventScroll: true }); } catch (err) { rowB.focus(); }
    }, smooth ? 200 : 0);
  }, true);

  window.addEventListener('pagehide', function () {
    Array.prototype.forEach.call(document.querySelectorAll('form.js_frm_search'), function (form) { setOpen(form, false); });
  });

  /* initial clear-button state for a pre-filled field (search results page) */
  Array.prototype.forEach.call(document.querySelectorAll('form[data-bt-search]'), toggleClear);

  window.btPredictive = { search: search, open: function (form) { setOpen(form, true); }, close: function (form) { setOpen(form, false); }, splitTitle: splitTitle };
})();
